import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { Nullable } from '@clearverify/types';

import {
  DisclaimerInfoFragment,
  DisclaimersListDocument,
  useDisclaimerUpdateMutation,
} from 'src/api';
import { AutocompleteField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { DisclaimerEditFormFields, DisclaimerEditFormValues } from './types';
import { getDisclaimerEditValidationSchema } from './utils';

interface DisclaimerUpdateFormProps {
  closeModal: () => void;
  disclaimer: DisclaimerInfoFragment;
  disclaimersList: Nullable<DisclaimerInfoFragment[]>;
}

export const DisclaimerUpdateForm: React.FC<DisclaimerUpdateFormProps> = ({
  closeModal,
  disclaimer,
  disclaimersList,
}) => {
  const { createApolloContext } = useNotification();

  const validationSchemaDisclaimers =
    disclaimersList?.filter(disclaimerToValidate => disclaimerToValidate.id !== disclaimer.id) ??
    [];

  const [updateDisclaimer] = useDisclaimerUpdateMutation({
    refetchQueries: [DisclaimersListDocument],
    context: createApolloContext({ successMessage: 'Disclaimer successfully updated' }),
  });

  const initialValues: DisclaimerEditFormValues = {
    [DisclaimerEditFormFields.name]: disclaimer.name,
    [DisclaimerEditFormFields.disclaimer]: disclaimer.disclaimer,
    [DisclaimerEditFormFields.titles]: disclaimer.titles,
  };

  const handleSubmit = React.useCallback(
    async (formData: DisclaimerEditFormValues) => {
      try {
        await updateDisclaimer({
          variables: {
            where: { id: disclaimer.id },
            data: {
              name: formData.name,
              disclaimer: formData.disclaimer,
              titles: {
                set: formData.titles.filter(isNotNil),
              },
            },
          },
        });
        closeModal();
      } catch (error) {
        console.error(error);
      }
    },
    [closeModal, disclaimer.id, updateDisclaimer],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, submitting: true }}
      keepDirtyOnReinitialize
      validate={validateWithYupSchema(
        getDisclaimerEditValidationSchema(validationSchemaDisclaimers),
      )}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2} mb={4}>
              <Grid size={12}>
                <Field
                  label="Disclaimer name"
                  name={DisclaimerEditFormFields.name}
                  fullWidth
                  required
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  label="Titles"
                  name={DisclaimerEditFormFields.titles}
                  placeholder="Type Position title, then press “enter”"
                  fullWidth
                  required
                  disabled={submitting}
                  freeSolo
                  multiple
                  component={AutocompleteField}
                  options={[]}
                />
                <Typography sx={{ marginLeft: '12px' }} variant="chip" color="GrayText">
                  To add several Titles, press “enter” after each title.
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography mb={2} variant="subtitle1">
                  Employer Disclaimer
                </Typography>
                <Field
                  label="Disclaimer"
                  name={DisclaimerEditFormFields.disclaimer}
                  fullWidth
                  multiline
                  required
                  minRows={3}
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Button variant="outlined" color="error" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting}
              >
                Save changes
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
